import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Youtube, Facebook, Instagram } from 'lucide-react';

const MobileMenuOverlay = styled.div<{ isOpen: boolean; theme: any }>`
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ theme }) => theme.colors.backgroundDark};
    z-index: 999;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 2rem;
`;

const MenuContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    width: 100%;
`;

const MobileMenuList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    flex: 1;
    justify-content: center;
`;

const MobileMenuItem = styled.li`
    margin: 0;
`;

const MobileMenuLink = styled(Link)<{ $isActive: boolean }>`
    text-decoration: none;
    color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.text)};
    font-size: 2rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: color 0.2s ease;
    padding: 0;
    width: 100%;
    text-align: left;
    display: block;

    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const SocialLinks = styled.div`
    display: flex;
    justify-content: center;
    gap: 2rem;
    align-items: center;
    padding-bottom: 2rem;
`;

const SocialLink = styled.a<{ theme: any }>`
    color: ${({ theme }) => theme.colors.text};
    transition: color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        color: ${({ theme }) => theme.colors.primary};
    }

    svg {
        width: 28px;
        height: 28px;
    }
`;

interface MobileMenuProps {
    isOpen: boolean;
    menuItems: Array<{ label: string; path: string }>;
    onClose: () => void;
    currentPath: string;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
    isOpen,
    menuItems,
    onClose,
    currentPath,
}) => {
    return (
        <MobileMenuOverlay isOpen={isOpen} onClick={onClose}>
            <MenuContent>
                <MobileMenuList>
                    {menuItems.map((item) => (
                        <MobileMenuItem key={item.path}>
                            <MobileMenuLink
                                to={item.path}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                }}
                                $isActive={currentPath === item.path}
                            >
                                {item.label}
                            </MobileMenuLink>
                        </MobileMenuItem>
                    ))}
                </MobileMenuList>

                <SocialLinks>
                    <SocialLink
                        href="https://www.instagram.com/the.butterscotch.boys/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Instagram />
                    </SocialLink>
                    <SocialLink
                        href="https://www.youtube.com/@TheButterscotchBoys"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Youtube />
                    </SocialLink>
                    <SocialLink
                        href="https://www.facebook.com/profile.php?id=61567843858058"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Facebook />
                    </SocialLink>
                </SocialLinks>
            </MenuContent>
        </MobileMenuOverlay>
    );
};
